import React from "react";
import "./SmartByBondList.css";
import {Accordion} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLightbulb, faLink, faLinkSlash} from "@fortawesome/free-solid-svg-icons";
import Badge from "react-bootstrap/Badge";
import BondCard from "../Cards/BondCard";
import {truncateFirmwareVersion, formatTargetName} from "../../../../../lib/ui/badgeUtils";
import { useBrandingProfile } from "../../../hooks/useBrandingProfile";

function SBBAccordionItem({ bond, bondData, isLoading, error }) {
    const profile = useBrandingProfile(bond.bond_id);
    const connectivity = bondData?.connectivity || {};
    const sysVersion = bondData?.sys?.version || {};
    const showBrandBadge = profile && profile.brand?.display_name && profile.brand.display_name !== 'Olibra';

    return (
        <Accordion.Item eventKey={bond.bond_id}>
            <Accordion.Header>
                <div className="d-flex flex-column w-100" style={{gap: '0.25rem'}}>
                    {/* First row: connectivity + bond ID + badges */}
                    <div className="d-flex flex-wrap align-items-center w-100" style={{gap: '0.5rem'}}>
                        <div className="d-flex align-items-center">
                            <h5 className="mb-0">
                                {connectivity.connected !== undefined && (
                                        <Badge bg={connectivity.connected ? "success" : "danger"}>
                                            <FontAwesomeIcon icon={connectivity.connected ? faLink : faLinkSlash} />
                                        </Badge>
                                    ) ||
                                    <FontAwesomeIcon
                                        icon={faLightbulb}
                                        className="me-2"
                                        style={{ color: connectivity.connected ? '#4caf7d' : '#e57373' }}
                                    />}
                                <span className="fw-bold"> {bond.bond_id}</span>
                            </h5>
                        </div>
                        <div className="d-flex align-items-center ms-auto" style={{gap: '0.25rem'}}>
                            {sysVersion.make && sysVersion.model && (
                                <Badge bg="dark">{sysVersion.make} {sysVersion.model}</Badge>
                            )}
                            {sysVersion.fw_ver && (
                                <Badge bg="secondary" title={sysVersion.fw_ver}>
                                    {truncateFirmwareVersion(sysVersion.fw_ver)}
                                </Badge>
                            )}
                            {sysVersion.target && (
                                <Badge bg="info">{formatTargetName(sysVersion.target)}</Badge>
                            )}
                        </div>
                    </div>

                    {/* Second row: brand info (only if custom brand) — right-aligned */}
                    {showBrandBadge && (
                        <div className="d-flex align-items-center ms-auto" style={{gap: '0.4rem'}}>
                            <Badge bg="warning" text="dark" style={{ fontSize: '0.72rem' }}>{profile.name}</Badge>
                            <span className="small text-muted">{profile.brand.display_name}</span>
                            {profile.brand?.images?.logo?.['48x48'] && (
                                <div style={{
                                    width: '32px', height: '32px',
                                    backgroundColor: 'white',
                                    borderRadius: '5px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: '1px solid #dee2e6',
                                    flexShrink: 0,
                                }}>
                                    <img
                                        src={profile.brand.images.logo['48x48']}
                                        alt={profile.brand.display_name}
                                        style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Accordion.Header>
            <Accordion.Body className="p-2">
                {isLoading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger">
                        Error loading Smart by Bond device {bond.bond_id}: {error}
                    </div>
                ) : bondData ? (
                    <BondCard bondData={bondData} hideBridge={true}/>
                ) : (
                    <div className="text-center text-muted p-3">
                        No data available for {bond.bond_id}
                    </div>
                )}
            </Accordion.Body>
        </Accordion.Item>
    );
}

function SmartByBondList({smartByBonds, bondsData, bondsLoading, bondsError}) {
    if (!smartByBonds || smartByBonds.length === 0) {
        return null;
    }

    return (
        <Accordion defaultActiveKey="0" className="mb-2">
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div>
                            <FontAwesomeIcon icon={faLightbulb} className="me-2"/>
                            Smart by Bond
                        </div>
                        <Badge bg="dark">
                            {smartByBonds.length}
                        </Badge>
                    </div>
                </Accordion.Header>
                <Accordion.Body className="p-2">
                    <Accordion>
                        {smartByBonds.map((bond) => (
                            <SBBAccordionItem
                                key={bond.bond_id}
                                bond={bond}
                                bondData={bondsData[bond.bond_id]}
                                isLoading={bondsLoading[bond.bond_id]}
                                error={bondsError[bond.bond_id]}
                            />
                        ))}
                    </Accordion>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}

export default SmartByBondList;
